<template>
  <v-dialog
    v-model="dialog"
    persistent
    max-width="600px"
    overlay-opacity="0.85"
    content-class="modern-profile-dialog"
  >
    <v-card class="rounded-xl" elevation="16">
      <!-- Header -->
      <v-card-title class="editor-header d-flex justify-space-between align-center px-6 py-4">
        <div class="text-h5 font-weight-bold white--text">
          <v-icon large color="white" class="mr-3">mdi-account-circle</v-icon>
          Edit Profile
        </div>
        <v-btn icon @click="onClose" class="close-btn" color="white" depressed large>
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-divider></v-divider>

      <!-- Help -->
      <v-card-text class="px-6 pt-5 pb-1 text-body-1 text--secondary">
        <div class="d-flex align-center">
          <v-icon color="primary" class="mr-2">mdi-information-outline</v-icon>
          Update your profile image and personal information
        </div>
      </v-card-text>

      <!-- Error/Success -->
      <v-expand-transition>
        <v-card-text class="px-6 py-0" v-if="message">
          <v-alert
            :type="messageType"
            class="mb-4 elevation-2"
            transition="scale-transition"
            :icon="messageType === 'success' ? 'mdi-check-circle' : 'mdi-alert-circle'"
          >
            {{ message }}
          </v-alert>
        </v-card-text>
      </v-expand-transition>

      <!-- Form -->
      <v-card-text class="px-6 py-2">
        <v-row>
          <!-- Avatar -->
          <v-col cols="12" class="d-flex flex-column align-center py-4">
            <div class="text-subtitle-1 font-weight-medium primary--text mb-3">
              Profile Picture
            </div>
            <editable-avatar
              :image="avatarDisplayImage"
              :onImageSelected="onImageSelected"
              size="140"
              class="elevation-4 rounded-circle"
            />
          </v-col>

          <!-- First Name -->
          <v-col cols="12" md="6" class="py-2">
            <v-text-field
              v-model="profile.first_name"
              label="First Name*"
              outlined
              dense
              prepend-inner-icon="mdi-account-outline"
              :rules="[rules.required, rules.noXSS]"
              @input="onChange"
              class="modern-input"
            />
          </v-col>

          <!-- Last Name -->
          <v-col cols="12" md="6" class="py-2">
            <v-text-field
              v-model="profile.last_name"
              label="Last Name*"
              outlined
              dense
              prepend-inner-icon="mdi-account-outline"
              :rules="[rules.required, rules.noXSS]"
              @input="onChange"
              class="modern-input"
            />
          </v-col>

          <!-- Address -->
          <v-col cols="12" class="py-2">
            <v-text-field
              v-model="profile.address"
              label="Address*"
              outlined
              dense
              prepend-inner-icon="mdi-map-marker"
              :rules="[rules.required, rules.noXSS]"
              @input="onChange"
              class="modern-input"
            />
          </v-col>
        </v-row>
      </v-card-text>

      <!-- Actions -->
      <v-card-actions class="px-6 pb-6 pt-4 d-flex justify-space-between">
        <v-btn text @click="onClose" color="grey darken-1" class="px-6" large>
          <v-icon left>mdi-arrow-left</v-icon>
          Cancel
        </v-btn>
        <v-btn
          color="#3b7ca1"
          @click="onSave"
          depressed
          width="160"
          :loading="loading"
          :disabled="!profile.first_name || !profile.last_name || !profile.address || !profile.image && !selectedImage"
          class="px-6 save-btn"
          large
          dark
        >
          <template v-slot:loader>
            <span class="custom-loader">
              <v-icon light>mdi-autorenew</v-icon>
            </span>
          </template>
          <v-icon left>mdi-content-save</v-icon>
          Save Profile
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions, mapState } from "vuex";
import EditableAvatar from "@/components/EditableAvatar.vue";

// 🛡️ Helper: sanitize user input before saving
function cleanProfile(profile) {
  const sanitize = str =>
    str ? str.replace(/[<>]/g, "") : ""; // remove < and >
  return {
    ...profile,
    first_name: sanitize(profile.first_name),
    last_name: sanitize(profile.last_name),
    address: sanitize(profile.address)
  };
}

export default {
  components: { EditableAvatar },
  props: {
    dialog: { type: Boolean, default: false },
    onClose: { type: Function }
  },
  data() {
    return {
      message: null,
      messageType: "error",
      loading: false,
      selectedImage: null,
      selectedImagePreview: null,
      rules: {
        required: value => !!value || "This field is required",
        noXSS: value =>
          !/[<>]/.test(value) || "Invalid characters detected"
      },
      profile: {}
    };
  },
  methods: {
    ...mapActions("auth", { updateProfile: "updateProfile" }),
    onChange() {
      this.message = null;
    },
    onImageSelected(image) {
      this.selectedImage = image;
      // Create a preview URL for the selected image
      if (image instanceof File) {
        this.selectedImagePreview = URL.createObjectURL(image);
      } else {
        this.selectedImagePreview = image;
      }
      this.message = null;
    },
    async onSave() {
      // 🔒 Clean before save
      const safeProfile = cleanProfile(this.profile);

      if (!safeProfile.first_name || !safeProfile.last_name || !safeProfile.address) {
        this.message = "All fields are required";
        this.messageType = "error";
        return;
      }

      const formData = new FormData();
      if (this.selectedImage) {
        formData.append("image", this.selectedImage);
      }
      formData.append("first_name", safeProfile.first_name);
      formData.append("last_name", safeProfile.last_name);
      formData.append("address", safeProfile.address);

      this.loading = true;
      try {
        await this.updateProfile(formData);
        this.message = "Profile updated successfully!";
        this.messageType = "success";
        // Reset selected image after successful save
        this.selectedImage = null;
        this.selectedImagePreview = null;
        setTimeout(() => {
          this.onClose();
        }, 1500);
      } catch (error) {
        this.message = error.response?.data?.message || "An error occurred";
        this.messageType = "error";
      } finally {
        this.loading = false;
      }
    }
  },
  computed: {
    ...mapState("auth", { myProfile: "profile" }),
    avatarDisplayImage() {
      // If there's a selected image preview, show that
      if (this.selectedImagePreview) {
        return this.selectedImagePreview;
      }
      // Otherwise show the profile image
      return this.profile.midThumbnail || this.profile.image;
    }
  },
  watch: {
    myProfile(newValue) {
      this.profile = { ...newValue };
      // Reset image preview when profile changes
      this.selectedImagePreview = null;
      this.selectedImage = null;
    }
  },
  mounted() {
    this.profile = { ...this.myProfile };
    this.message = null;
  },
  beforeDestroy() {
    // Clean up object URLs to prevent memory leaks
    if (this.selectedImagePreview) {
      URL.revokeObjectURL(this.selectedImagePreview);
    }
  }
};
</script>

<style scoped>
.modern-profile-dialog {
  border-radius: 16px !important;
  overflow: hidden;
}

.editor-header {
  background: linear-gradient(135deg, #3b7ca1 0%, #084468 100%);
}

.close-btn {
  transition: all 0.3s ease;
}

.close-btn:hover {
  transform: rotate(90deg);
  background: rgba(255,255,255,0.2) !important;
}

.modern-input::v-deep .v-input__slot {
  border-radius: 10px !important;
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.5, 1);
  background-color: white !important;
}

.save-btn {
  background: linear-gradient(135deg, #3b7ca1 0%, #084468 100%) !important;
  box-shadow: 0 4px 12px rgba(59, 124, 161, 0.4) !important;
  transition: all 0.3s ease;
  border: none !important;
  color: white !important;
  font-weight: 600 !important;
  letter-spacing: 0.5px;
}

.save-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(59, 124, 161, 0.5) !important;
  background: linear-gradient(135deg, #4a8db5 0%, #0a557d 100%) !important;
}

.save-btn:active:not(:disabled) {
  transform: translateY(0);
}

.save-btn:disabled {
  opacity: 0.6;
  transform: none !important;
  box-shadow: 0 2px 8px rgba(59, 124, 161, 0.2) !important;
  cursor: not-allowed;
}

.custom-loader {
  animation: loader 1s infinite;
  display: flex;
}

@keyframes loader {
  from {
    transform: rotate(0);
  }
  to {
    transform: rotate(360deg);
  }
}

/* Scrollbar styling */
.modern-profile-dialog::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

.modern-profile-dialog::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 10px;
}

.modern-profile-dialog::-webkit-scrollbar-thumb {
  background: linear-gradient(135deg, #3b7ca1 0%, #084468 100%);
  border-radius: 10px;
}

.modern-profile-dialog::-webkit-scrollbar-thumb:hover {
  background: #3a7bd5;
}
</style>